import { RestaurantLogger, extractErrorContext } from '@restaurant/logging'
import { buildWebContext } from './error-context-web'

// Injected at build time by vite (define: __APP_VERSION__ = git short sha).
// Guarded with `typeof` at the use site for the test/dev path where the
// define may be absent.
declare const __APP_VERSION__: string

const INSTALL_ID_KEY = 'webeatery:install_id'

/**
 * Known third-party / browser-extension error signatures with zero diagnostic
 * value for us. Matched against the derived error message in the global
 * handlers below and dropped before logging, so an injected script can't flood
 * the production logger DB. Add new signatures here as they're identified.
 *
 * Origin: VPS log briefing 2026-07-06 — a single client emitted 20,581 copies
 * of the "Object Not Found Matching Id" signature in ~16s (an in-app-webview /
 * extension bridge rejecting the same Promise), 97% of the day's web log volume.
 */
const NOISE_SIGNATURES: RegExp[] = [
  /Object Not Found Matching Id/i,
]

/** True when a message matches a known-noise third-party signature. */
export function isNoiseSignature(message: string): boolean {
  return NOISE_SIGNATURES.some((re) => re.test(message))
}

/**
 * True when a `window` ErrorEvent's `filename` is the page document itself
 * rather than a script URL. Our bundle always reports `/assets/index-*.js`;
 * a document-URL filename means the throwing code was injected into the page
 * by the browser (a WKUserScript — Chrome iOS's `__gCrWeb` autofill/translate
 * bundle) or an extension, which we cannot fix and whose frames match no
 * build of ours. The filename is the URL at injection time, so after an SPA
 * navigation it can differ from the current pathname (seen: filename `/menu`
 * while on `/cart`) — the test is same-origin + not a script file, not
 * path equality.
 *
 * Origin: VPS log briefing 2026-08-25 — 13 "Maximum call stack size exceeded"
 * RangeErrors (2 sessions, both Chrome iOS 151.0.7922.112) with frames at
 * lines 187/194/224 of a 107-line bundle. The shopper checked out fine.
 */
export function isInjectedScriptError(filename: string | undefined, href: string): boolean {
  if (!filename) return false
  try {
    const f = new URL(filename)
    const page = new URL(href)
    return f.origin === page.origin && !/\.[cm]?js$/i.test(f.pathname)
  } catch {
    return false
  }
}

/**
 * Stable per-install id, persisted in localStorage so anonymous sessions
 * from the same browser stay attributable across launches. Generated once.
 */
function getWebInstallId(): string {
  try {
    let id = localStorage.getItem(INSTALL_ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(INSTALL_ID_KEY, id)
    }
    return id
  } catch {
    return 'unknown'
  }
}

export function createWebLogger(): RestaurantLogger | null {
  // Skip the prod log pipeline when running `vite dev` — the browser DevTools console is
  // the source of truth locally, and dev logs otherwise pollute the production logger DB.
  // Opt back in with VITE_FORCE_LOGGING=1 if you specifically need to exercise logging in dev.
  if (import.meta.env.DEV && import.meta.env.VITE_FORCE_LOGGING !== '1') {
    return null
  }

  const logger = RestaurantLogger.create({
    service: 'webeatery-web',
    platform: 'web',
    flushEndpoint: `${import.meta.env.VITE_FLASK_BASE_URL || 'https://api.thalione.tech'}/api/v1/logs/flush`,
    productKey: import.meta.env.VITE_OBSERVABILITY_PRODUCT_KEY || undefined,
    // Attribution attached to every entry (context column) so anonymous
    // telemetry/errors are traceable to a build + install.
    context: {
      app_version:
        typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
      install_id: getWebInstallId(),
    },
  })

  // Flush logs when tab is hidden or page is unloading
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') logger.flush()
  })
  window.addEventListener('beforeunload', () => logger.flush())

  // Global error capture
  window.addEventListener('unhandledrejection', (e) => {
    const err = e.reason instanceof Error ? e.reason : new Error(String(e.reason))
    if (isNoiseSignature(err.message)) return
    logger.applicationError(err, {
      ...extractErrorContext(e.reason),
      ...buildWebContext({ reason_kind: typeof e.reason }),
    })
  })
  window.addEventListener('error', (e) => {
    // Cross-origin script errors are sanitized by the browser to a bare
    // "Script error." with no error object, filename, or line — zero
    // diagnostic value. Skip them instead of logging unactionable noise.
    const isOpaqueCrossOrigin =
      !e.error &&
      (e.message === 'Script error.' || e.message === 'Script error') &&
      !e.filename &&
      (!e.lineno || e.lineno === 0)
    if (isOpaqueCrossOrigin) return

    const err = e.error ?? new Error(e.message)
    if (isNoiseSignature(err.message) || isNoiseSignature(e.message)) return
    // Browser-injected user scripts (Chrome iOS `__gCrWeb`, extensions) throw
    // with the page URL as filename. Keep a warn for volume tracking, but not
    // an error — nothing in our bundle threw and nothing can be fixed here.
    if (isInjectedScriptError(e.filename, window.location.href)) {
      logger.warn(`Injected script error: ${err.message}`, {
        domain: 'error',
        error_name: err?.name,
        injected_script: true,
        ...buildWebContext({ filename: e.filename, lineno: e.lineno }),
      })
      return
    }
    logger.applicationError(err, {
      ...extractErrorContext(e.error),
      ...buildWebContext({ filename: e.filename, lineno: e.lineno }),
    })
  })
  // Vite emits `vite:preloadError` on `window` when a dynamic-import chunk
  // fails to load (e.g., Safari TypeError("Load failed")). The event's
  // `payload` is the underlying Error. We extract the chunk URL from
  // the standard Vite message format for structured logging.
  // Verified against node_modules/vite/dist/node/chunks/dep-*.js:
  //   `const e = new Event("vite:preloadError", {...}); e.payload = err;`
  window.addEventListener('vite:preloadError', (e: Event) => {
    const ev = e as Event & { payload?: Error }
    const err = ev.payload ?? new Error('Vite preload error (no payload)')
    const chunk_url = err.message.match(/https?:\/\/\S+/)?.[0]
    logger.applicationError(err, buildWebContext({ chunk_url, vite_preload: true }))
  })

  return logger
}
